import type {
	ActivityItem,
	ChartDataPoint,
	Document,
	GraphEdge,
	GraphNode,
	Proposal,
	QAMessage,
	SearchResult,
	Stats,
} from "@/types";

export const mockStats: Stats = {
	totalDocuments: 1247,
	pendingReview: 23,
	processedThisWeek: 47,
	acceptanceRate: 0.73,
	searchHitRate: 0.89,
	totalConcepts: 3421,
	totalEdges: 12847,
	totalCommunities: 89,
	aiCostThisMonth: 12.47,
};

export const mockDocuments: Document[] = [
	{
		id: "doc_01",
		title: "The Architecture of Open Source Applications",
		category: "article",
		status: "awaiting_approval",
		processingStatus: "completed",
		location: "shortlist",
		tags: ["software-architecture", "open-source", "design-patterns"],
		wordCount: 4200,
		author: "Michael Feathers",
		sourceUrl: "https://aosabook.org/en/index.html",
		summary:
			"An exploration of how successful open source projects structure their code, covering architectural patterns from NGINX to Git.",
		createdAt: "2026-03-28T10:00:00Z",
		updatedAt: "2026-03-29T14:30:00Z",
		content: `The architecture of a software system is shaped by many forces: the requirements it must fulfill, the environment in which it operates, and the experiences and preferences of its creators. This book explores the architectures of several well-known open source applications, examining how they work and why they were designed the way they were.

When we look at open source applications, we see that the key insight is that architecture is not about the technology choices — it's about the constraints and trade-offs that shaped the system over time. Every successful project found its own balance between simplicity and power, between flexibility and performance.

NGINX, for example, was designed from the ground up to handle thousands of concurrent connections efficiently. Its event-driven architecture avoids the overhead of thread-per-connection models, using a small number of worker processes that each handle many connections through non-blocking I/O.

The modular pipeline architecture allows functionality to be added through phases — each request passes through a series of processing phases, and modules can hook into any phase to add behavior. This design achieves remarkable extensibility without sacrificing performance.

Git takes a fundamentally different approach. Rather than treating version control as a database of changes, Git stores content-addressable snapshots. Every commit is a complete snapshot of the repository, referenced by its SHA-1 hash. This content-addressable storage model means that data integrity is built into the very foundation of the system.

The directed acyclic graph of commits enables Git's powerful branching model. Because branches are simply pointers to commits, creating and switching branches is nearly instantaneous. This architectural decision transformed how developers collaborate, making feature branches and pull requests practical for the first time.

These examples illustrate a broader principle: the best architectures emerge from a deep understanding of the problem domain, not from applying patterns mechanically. Architecture is about making the right trade-offs for your specific context.`,
	},
	{
		id: "doc_02",
		title: "Attention Is All You Need",
		category: "pdf",
		status: "queued",
		processingStatus: "queued",
		location: "new",
		tags: ["machine-learning", "transformers", "nlp", "deep-learning"],
		wordCount: 8900,
		author: "Vaswani et al.",
		sourceUrl: "https://arxiv.org/abs/1706.03762",
		summary:
			"The landmark paper introducing the Transformer architecture, which replaced recurrent models with self-attention mechanisms.",
		createdAt: "2026-03-25T08:00:00Z",
		updatedAt: "2026-03-25T08:00:00Z",
	},
	{
		id: "doc_03",
		title: "Building a Second Brain - Tiago Forte",
		category: "podcast",
		status: "committed",
		processingStatus: "completed",
		location: "archive",
		tags: ["productivity", "note-taking", "pkm", "knowledge-management"],
		wordCount: 12400,
		author: "Tiago Forte",
		sourceUrl: "https://podcasts.example.com/second-brain-ep42",
		summary:
			"Tiago Forte discusses the CODE methodology for personal knowledge management and how digital tools can augment human cognition.",
		createdAt: "2026-03-20T15:00:00Z",
		updatedAt: "2026-03-22T11:00:00Z",
	},
	{
		id: "doc_04",
		title: "How Stripe Builds APIs",
		category: "article",
		status: "awaiting_approval",
		processingStatus: "completed",
		location: "shortlist",
		tags: ["api-design", "stripe", "developer-experience", "rest"],
		wordCount: 3100,
		author: "Paul Asjes",
		sourceUrl: "https://stripe.com/blog/api-design",
		summary:
			"An inside look at Stripe's API design principles, versioning strategy, and how they maintain backward compatibility.",
		createdAt: "2026-03-30T09:00:00Z",
		updatedAt: "2026-04-01T16:00:00Z",
	},
	{
		id: "doc_05",
		title: "The Pragmatic Programmer - Highlights",
		category: "highlight",
		status: "committed",
		processingStatus: "completed",
		location: "archive",
		tags: ["programming", "software-craft", "best-practices"],
		wordCount: 850,
		author: "Hunt & Thomas",
		sourceUrl: "https://pragprog.com/titles/tpp20/",
		createdAt: "2026-03-15T12:00:00Z",
		updatedAt: "2026-03-18T09:00:00Z",
	},
	{
		id: "doc_06",
		title: "Designing Data-Intensive Applications",
		category: "pdf",
		status: "proposing",
		processingStatus: "processing",
		location: "shortlist",
		tags: ["distributed-systems", "databases", "scalability"],
		wordCount: 15600,
		author: "Martin Kleppmann",
		sourceUrl: "https://dataintensive.net/",
		createdAt: "2026-03-10T07:00:00Z",
		updatedAt: "2026-03-28T20:00:00Z",
	},
	{
		id: "doc_07",
		title: "Rich Hickey - Simple Made Easy",
		category: "video",
		status: "committed",
		processingStatus: "completed",
		location: "archive",
		tags: ["simplicity", "complexity", "clojure", "software-design"],
		wordCount: 6200,
		author: "Rich Hickey",
		sourceUrl: "https://www.infoq.com/presentations/Simple-Made-Easy/",
		createdAt: "2026-02-28T14:00:00Z",
		updatedAt: "2026-03-05T10:00:00Z",
	},
	{
		id: "doc_08",
		title: "The Bitter Lesson - Rich Sutton",
		category: "article",
		status: "skipped",
		processingStatus: "completed",
		location: "archive",
		tags: ["ai", "machine-learning", "research", "scaling"],
		wordCount: 1800,
		author: "Rich Sutton",
		sourceUrl: "http://www.incompleteideas.net/IncIdeas/BitterLesson.html",
		createdAt: "2026-03-01T11:00:00Z",
		updatedAt: "2026-03-03T09:00:00Z",
	},
];

const firstMockDocument = mockDocuments[0];

if (firstMockDocument === undefined) {
	throw new Error("mockDocuments must include at least one document");
}

export const mockCurrentDocument = firstMockDocument;

export const mockCurrentProposal: Proposal = {
	id: "prop_01",
	documentId: "doc_01",
	modelId: "gpt-5.1-mini",
	candidates: [
		{
			index: 1,
			text: "The key insight is that architecture is not about the technology choices \u2014 it's about the constraints and trade-offs that shaped the system over time.",
			confidence: 0.94,
			status: "pending",
			rationale:
				"Core thesis statement that encapsulates the article's main argument about software architecture being driven by constraints rather than technology.",
			suggestedTags: ["architecture", "trade-offs"],
			charStart: 310,
			charEnd: 460,
		},
		{
			index: 2,
			text: "Its event-driven architecture avoids the overhead of thread-per-connection models, using a small number of worker processes that each handle many connections through non-blocking I/O.",
			confidence: 0.88,
			status: "pending",
			rationale:
				"Precise technical description of NGINX's architectural innovation that made it stand out from Apache and other web servers.",
			suggestedTags: ["nginx", "event-driven", "concurrency"],
			charStart: 680,
			charEnd: 862,
		},
		{
			index: 3,
			text: "Rather than treating version control as a database of changes, Git stores content-addressable snapshots. Every commit is a complete snapshot of the repository, referenced by its SHA-1 hash.",
			confidence: 0.91,
			status: "pending",
			rationale:
				"Fundamental design decision that differentiates Git from other VCS systems and enables its performance characteristics.",
			suggestedTags: ["git", "content-addressable", "version-control"],
			charStart: 1095,
			charEnd: 1290,
		},
		{
			index: 4,
			text: "Because branches are simply pointers to commits, creating and switching branches is nearly instantaneous. This architectural decision transformed how developers collaborate.",
			confidence: 0.82,
			status: "pending",
			rationale:
				"Connects the technical architecture to its impact on developer workflows and collaboration practices.",
			suggestedTags: ["git", "branching", "collaboration"],
			charStart: 1380,
			charEnd: 1555,
		},
		{
			index: 5,
			text: "The best architectures emerge from a deep understanding of the problem domain, not from applying patterns mechanically.",
			confidence: 0.86,
			status: "pending",
			rationale:
				"Concluding insight that serves as a practical takeaway for practitioners, warning against cargo-cult architecture.",
			suggestedTags: ["architecture", "domain-driven"],
			charStart: 1620,
			charEnd: 1740,
		},
	],
	summary:
		"This article examines how successful open source projects like NGINX and Git were architecturally shaped by their specific constraints and requirements, arguing that great architecture emerges from deep domain understanding rather than mechanical pattern application.",
	suggestedTags: ["software-architecture", "open-source", "design-patterns", "nginx", "git"],
};

export const mockSearchResults: SearchResult[] = [
	{
		id: "sr_01",
		documentId: "doc_01",
		title: "The Architecture of Open Source Applications",
		text: "The key insight is that architecture is not about the technology choices \u2014 it's about the constraints and trade-offs that shaped the system over time. Every successful project found its own balance between simplicity and power.",
		score: 0.94,
		pointType: "highlight",
		tags: ["software-architecture", "open-source"],
		category: "article",
	},
	{
		id: "sr_02",
		documentId: "doc_06",
		title: "Designing Data-Intensive Applications",
		text: "A data-intensive application is typically built from standard building blocks that provide commonly needed functionality. For example, many applications need to store data so that they can find it again later (databases), remember the result of an expensive operation to speed up reads (caches).",
		score: 0.87,
		pointType: "raw_chunk",
		tags: ["distributed-systems", "databases"],
		category: "pdf",
	},
	{
		id: "sr_03",
		documentId: "doc_04",
		title: "How Stripe Builds APIs",
		text: "Stripe's API versioning strategy ensures backward compatibility while allowing the API to evolve. Each API version is a complete snapshot of the API surface, and customers can upgrade at their own pace.",
		score: 0.82,
		pointType: "highlight",
		tags: ["api-design", "stripe"],
		category: "article",
	},
	{
		id: "sr_04",
		documentId: "doc_07",
		title: "Rich Hickey - Simple Made Easy",
		text: "Simplicity is a prerequisite for reliability. Simple things are easier to reason about, easier to test, and easier to maintain. Complexity is the enemy of reliability.",
		score: 0.78,
		pointType: "highlight",
		tags: ["simplicity", "software-design"],
		category: "video",
	},
	{
		id: "sr_05",
		documentId: "doc_03",
		title: "Building a Second Brain - Tiago Forte",
		text: "The CODE methodology \u2014 Capture, Organize, Distill, Express \u2014 provides a systematic framework for turning information into actionable knowledge. The key is to progressively summarize content.",
		score: 0.71,
		pointType: "summary",
		tags: ["productivity", "pkm"],
		category: "podcast",
	},
];

export const mockConversation: QAMessage[] = [
	{
		id: "qa_01",
		role: "user",
		content: "What are the main architectural patterns used by successful open source projects?",
		timestamp: "2026-04-03T14:00:00Z",
	},
	{
		id: "qa_02",
		role: "assistant",
		content: `Based on your knowledge base, successful open source projects employ several key architectural patterns:

**1. Event-Driven Architecture** \u2014 NGINX uses an event-driven model with non-blocking I/O to handle thousands of concurrent connections efficiently, avoiding the overhead of thread-per-connection models.

**2. Content-Addressable Storage** \u2014 Git stores complete snapshots referenced by SHA-1 hashes rather than deltas, building data integrity into the foundation and enabling its powerful branching model.

**3. Modular Pipeline Architecture** \u2014 NGINX processes requests through a series of phases, allowing modules to hook into any phase. This achieves extensibility without sacrificing performance.

The overarching principle is that **architecture should emerge from domain understanding, not mechanical pattern application**. Each project found its own balance between simplicity, power, flexibility, and performance.`,
		citations: [
			{
				documentId: "doc_01",
				title: "The Architecture of Open Source Applications",
				text: "The key insight is that architecture is not about the technology choices...",
			},
			{
				documentId: "doc_07",
				title: "Rich Hickey - Simple Made Easy",
				text: "Simplicity is a prerequisite for reliability...",
			},
		],
		relatedConcepts: [
			"Event-Driven Architecture",
			"Content-Addressable Storage",
			"Modular Design",
			"Software Architecture",
			"Non-blocking I/O",
		],
		followUpQuestions: [
			"How does NGINX's event-driven model compare to Node.js?",
			"What trade-offs did Git make with content-addressable storage?",
			"How do these patterns apply to modern microservices?",
		],
		timestamp: "2026-04-03T14:00:05Z",
	},
];

export const mockGraphNodes: GraphNode[] = [
	{ id: "c_arch", type: "concept", label: "Software Architecture", mentionCount: 47 },
	{ id: "c_event", type: "concept", label: "Event-Driven", mentionCount: 23 },
	{ id: "c_git", type: "concept", label: "Git", mentionCount: 18 },
	{ id: "c_nginx", type: "concept", label: "NGINX", mentionCount: 15 },
	{ id: "c_api", type: "concept", label: "API Design", mentionCount: 31 },
	{ id: "c_dist", type: "concept", label: "Distributed Systems", mentionCount: 38 },
	{ id: "c_scale", type: "concept", label: "Scalability", mentionCount: 29 },
	{ id: "c_simple", type: "concept", label: "Simplicity", mentionCount: 21 },
	{ id: "c_perf", type: "concept", label: "Performance", mentionCount: 26 },
	{ id: "c_ml", type: "concept", label: "Machine Learning", mentionCount: 42 },
	{ id: "c_trans", type: "concept", label: "Transformers", mentionCount: 19 },
	{ id: "c_pkm", type: "concept", label: "Knowledge Management", mentionCount: 14 },
	{
		id: "d_01",
		type: "document",
		label: "Architecture of OSS Apps",
		description: "Michael Feathers",
	},
	{
		id: "d_02",
		type: "document",
		label: "Attention Is All You Need",
		description: "Vaswani et al.",
	},
	{ id: "d_04", type: "document", label: "How Stripe Builds APIs", description: "Paul Asjes" },
	{ id: "d_06", type: "document", label: "DDIA", description: "Kleppmann" },
	{ id: "d_07", type: "document", label: "Simple Made Easy", description: "Rich Hickey" },
	{ id: "h_01", type: "highlight", label: "Architecture = constraints + trade-offs" },
	{ id: "h_02", type: "highlight", label: "Event-driven avoids thread overhead" },
	{ id: "h_03", type: "highlight", label: "Content-addressable snapshots" },
];

export const mockGraphEdges: GraphEdge[] = [
	{ id: "e1", source: "d_01", target: "c_arch", type: "ABOUT", weight: 0.95 },
	{ id: "e2", source: "d_01", target: "c_event", type: "MENTIONS", weight: 0.8 },
	{ id: "e3", source: "d_01", target: "c_git", type: "MENTIONS", weight: 0.85 },
	{ id: "e4", source: "d_01", target: "c_nginx", type: "MENTIONS", weight: 0.85 },
	{ id: "e5", source: "c_arch", target: "c_dist", type: "RELATED_TO", weight: 0.7 },
	{ id: "e6", source: "c_arch", target: "c_scale", type: "RELATED_TO", weight: 0.65 },
	{ id: "e7", source: "c_arch", target: "c_simple", type: "RELATED_TO", weight: 0.6 },
	{ id: "e8", source: "c_event", target: "c_perf", type: "CAUSES", weight: 0.75 },
	{ id: "e9", source: "d_02", target: "c_ml", type: "ABOUT", weight: 0.95 },
	{ id: "e10", source: "d_02", target: "c_trans", type: "ABOUT", weight: 0.9 },
	{ id: "e11", source: "c_ml", target: "c_scale", type: "RELATED_TO", weight: 0.5 },
	{ id: "e12", source: "d_04", target: "c_api", type: "ABOUT", weight: 0.9 },
	{ id: "e13", source: "c_api", target: "c_arch", type: "RELATED_TO", weight: 0.6 },
	{ id: "e14", source: "d_06", target: "c_dist", type: "ABOUT", weight: 0.95 },
	{ id: "e15", source: "d_06", target: "c_scale", type: "ABOUT", weight: 0.85 },
	{ id: "e16", source: "d_07", target: "c_simple", type: "ABOUT", weight: 0.95 },
	{ id: "e17", source: "c_simple", target: "c_perf", type: "SUPPORTS", weight: 0.55 },
	{ id: "e18", source: "d_01", target: "h_01", type: "HAS_HIGHLIGHT", weight: 1 },
	{ id: "e19", source: "d_01", target: "h_02", type: "HAS_HIGHLIGHT", weight: 1 },
	{ id: "e20", source: "d_01", target: "h_03", type: "HAS_HIGHLIGHT", weight: 1 },
	{ id: "e21", source: "h_01", target: "c_arch", type: "MENTIONS", weight: 0.9 },
	{ id: "e22", source: "c_pkm", target: "c_arch", type: "RELATED_TO", weight: 0.4 },
	{ id: "e23", source: "c_trans", target: "c_perf", type: "RELATED_TO", weight: 0.45 },
];

export const mockActivity: ActivityItem[] = [
	{
		id: "act_01",
		type: "sync",
		title: "Reader Sync",
		description: "Fetched 5 new documents from Readwise Reader",
		timestamp: "2026-04-04T08:30:00Z",
	},
	{
		id: "act_02",
		type: "propose",
		title: "Proposals Generated",
		description: 'Generated 5 candidates for "Architecture of OSS Apps"',
		timestamp: "2026-04-04T08:31:00Z",
	},
	{
		id: "act_03",
		type: "approve",
		title: "Candidate Approved",
		description: 'Approved highlight #1 from "How Stripe Builds APIs"',
		timestamp: "2026-04-03T16:45:00Z",
	},
	{
		id: "act_04",
		type: "commit",
		title: "Highlights Published",
		description: '4 highlights published for "Simple Made Easy"',
		timestamp: "2026-04-03T16:00:00Z",
	},
	{
		id: "act_05",
		type: "index",
		title: "Document Indexed",
		description: '"DDIA" indexed: 42 chunks, 156 concepts',
		timestamp: "2026-04-03T14:20:00Z",
	},
	{
		id: "act_06",
		type: "search",
		title: "Search Query",
		description: '"architectural patterns in distributed systems"',
		timestamp: "2026-04-03T13:10:00Z",
	},
	{
		id: "act_07",
		type: "ask",
		title: "Question Asked",
		description: '"What are the main architectural patterns..."',
		timestamp: "2026-04-03T14:00:00Z",
	},
	{
		id: "act_08",
		type: "reject",
		title: "Candidate Rejected",
		description: 'Rejected highlight #3 from "Bitter Lesson" (too generic)',
		timestamp: "2026-04-02T10:30:00Z",
	},
];

export const mockWeeklyChart: ChartDataPoint[] = [
	{ date: "Mon", value: 8 },
	{ date: "Tue", value: 12 },
	{ date: "Wed", value: 5 },
	{ date: "Thu", value: 15 },
	{ date: "Fri", value: 7 },
	{ date: "Sat", value: 3 },
	{ date: "Sun", value: 0 },
];

export const mockMonthlyChart: ChartDataPoint[] = [
	{ date: "W1", value: 32 },
	{ date: "W2", value: 45 },
	{ date: "W3", value: 38 },
	{ date: "W4", value: 47 },
];

export const mockCostByModel: Array<{ model: string; cost: number }> = [
	{ model: "GPT-5.1 Mini", cost: 6.23 },
	{ model: "Embeddings L", cost: 3.84 },
	{ model: "Cohere Rerank", cost: 1.42 },
	{ model: "Deepgram", cost: 0.98 },
];

export const mockCategoryDistribution: Array<{
	name: string;
	value: number;
	fill: string;
}> = [
	{ name: "Articles", value: 485, fill: "#3b82f6" },
	{ name: "PDFs", value: 312, fill: "#f97316" },
	{ name: "Podcasts", value: 178, fill: "#ec4899" },
	{ name: "Videos", value: 124, fill: "#a855f7" },
	{ name: "Emails", value: 89, fill: "#06b6d4" },
	{ name: "RSS", value: 59, fill: "#eab308" },
];
